import React from 'react';
import Promise from 'bluebird';
import ReactDOM from 'react-dom';
import _ from 'underscore';
import {
  AudienceStepBase,
  AudienceCampaignErrorsView
} from '@bingads-webui-campaign/audience-campaign-shared';
import { withUiBlock } from '@bingads-webui/uiblocker';
import { DependencyResolver } from '@bingads-webui-campaign/dependency-resolver';

import template from './template.pug';
import { mergeI18n } from './utils/merge-i18n';
import { CrudAudienceAdReactWithContext } from './crud-audience-ad-react-with-context';
import { ResponsiveAdRepository } from './responsive-ad-repository';
import GenericImage from '../media/1.91to1.jpg';

/**
 * ResponsiveAdEditor class that extends {AudienceStepBase} class
 * The class defines the section of the setting Ads
 *
 * @class ResponsiveAdEditor
 * @extends {AudienceStepBase}
 */
export class ResponsiveAdEditor extends AudienceStepBase {
  initialize(options = {}) {
    this.options = options;
    this.renderMarker;
    this.isForVNext = options.isForVNext;
    this.isMultimediaAd = options.isMultimediaAd || false;
    this.isPlaceholderMMA = options.isPlaceholderMMA || false;
    this.perfMarker = options.perfMarker;
    this.perfMarker.willInit();
    this.wizardElement = options.wizardElement;
    this.enableOfflinePollingDelay = options.enableOfflinePollingDelay;

    this.viewContext = options.viewContext || {};
    this.serverContext = options.serverContext || {};
    this.pageContext = _.get(options.deps, 'pageContext') || options.pageContext;
    this.isGridDisplay = options.isGridDisplay;
    // this.isGridDisplay = false;
    this.isMobileUrlEnabled = options.isMobileUrlEnabled == null ?
      true : options.isMobileUrlEnabled;
    this.isImpressionTrackingUrlsEnabled =
      _.result(options, ['deps', 'pageContext', 'Permissions', 'IsImpressionTrackingUrlsEnabled'], false) ||
      _.result(options, ['pageContext', 'Permissions', 'IsImpressionTrackingUrlsEnabled'], false) ||
      options.isImpressionTrackingUrlsEnabled;
    this.ad = options.ad;
    this.biddingScheme = options.biddingScheme;
    this.newI18n = options.newI18n;
    this.i18n = DependencyResolver.get('component/i18n/loader!campaignui/feature/audience-campaign');
    mergeI18n(this.i18n, this.newI18n);

    this.i18nErrors = DependencyResolver.get('component/api-errors-campaign/index');
    if (_.isFunction(this.i18nErrors.getI18n)) {
      const i18nInstance = this.i18nErrors.getI18n();
      mergeI18n(this.i18n, i18nInstance ? i18nInstance.realI18n : i18nInstance);
    }
    this.scenario = options.scenario;
    this.logScenario = options.logScenario;
    this.activityLogger = this.logScenario.createAds.create();
    this.hasWebsiteImageOptedInObservable = options.hasWebsiteImageOptedInObservable;
    this.parsedTextMaxLengthExtension = options.parsedTextMaxLengthExtension;
    this.isRecommendation = options.isRecommendation;
    this.edm = options.edm;
    this.jsdata = options.jsdata;
    this.isImproveMMA = options.isImproveMMA;
    this.recommendationTargetAccountId = options.ad && options.ad.TargetAccountId;

    this.responsiveAdRepository = new ResponsiveAdRepository({
      viewContext: this.viewContext,
      isVideo: options.isVideo,
      campaignDomain: options.campaignDomain,
    });

    this.noCreatedAdErr = new AudienceCampaignErrorsView();

    this.crudReactComponent = React.createRef();
    this.onAdSaved = this.onAdSaved.bind(this);

    // configure parameters needed tfor stock image feature
    const config = DependencyResolver.get('component/config/index');
    const odataToken = config.get('odata.token');
    const odataPath = config.get('odata.url') + config.get('odata.path');
    const odataRootUrl = config.get('CampaignAzureDomain');
    this.permissions = config.get('Permissions');
    this.isVideo = options.isVideo;

    this.isOpenCreatePanel = options.isOpenCreatePanel;
    this.isLastStep = options.lastStep === 'ResponsiveAdEditor';
    this.applyActivity = options.applyActivity;
    this.isSwapStepsEnabled = this.permissions.IsAudienceCampaignSwapAdsBudgetAndBidsEnabled &&
                              this.scenario !== 'adgroup-creation';

    this.isAudienceCampaign = (_.result(this.viewContext.currentCampaign, 'CampaignType') === 'Audience' ||
      _.result(this.viewContext.currentCampaign, 'CampaignType') === 8 ||
      options.audienceCampaignType === 'Audience');
    this.isMultimediaAdinMSANEnabled = this.isAudienceCampaign &&
      this.permissions.IsAudienceCampaignMultiMediaAdsEnabled &&
      !options.isVideo;

    this.isMMAV2Enabled = this.isMultimediaAdinMSANEnabled && this.permissions.IsMMAV2Enabled && !this.permissions.IsAmplifyEnabled;

    this.isAdRecommendationMSANEnabled = this.isAudienceCampaign &&
      this.permissions.IsResponsiveAdRecommendationMSANEnabled;

    this.oDataConfig = {
      odataRootUrl,
      odataPath,
      odataToken,
    };

    // enable dynamic config for this flag.
    this.isFinalUrlImageRetrievalEnabled = this.isAudienceCampaign;

    // enable ad creator for MSAN and MMA
    this.isAdCreatorEnabled = this.isMultimediaAdinMSANEnabled || this.isMultimediaAd;
  }

  /**
   * class name of el
   */
  get className() {
    return this.isMultimediaAd ? 'crud-multimedia-ad' : '';
  }

  /**
   * Get the title of the step section.
   * This is automatically called from the wizard.
   *
   * @readonly
   * @returns {Object} returns object has a property text that is used for the title of the section.
   */
  get title() {
    const title = this.isSwapStepsEnabled ?
      this.i18n.getString('Audience_Campaign_Crud_Ad_Title_Step4') : this.i18n.getString('Audience_Campaign_Crud_Ad_Title');

    return {
      text: title,
    };
  }

  /**
   * Get the page title of the step section.
   * This is automatically called from the wizard.
   *
   * @readonly
   * @returns {Object} returns object has a property text that is used for the title of the section.
   */
  get pageTitle() {
    return {
      text: this.i18n.getString('Audience_Campaign_Crud_Ad_PageTitle'),
    };
  }

  onRenderedInDOM() {
    this.perfMarker.done();
  }

  remove() {
    const imageExperienceView = this.$('.react-image-experience')[0];
    if (imageExperienceView) {
      ReactDOM.unmountComponentAtNode(imageExperienceView);
    }
    super.remove();
  }

  /**
   * Renders the view
   *
   * @returns {View} returns the view itself
   */
  render() {
    if (this.options && this.options.perfMarker) {
      this.renderMarker = this.perfMarker.createChild('RenderCrudAd');
      this.renderMarker.willInit();
    }

    this.perfMarker.willRender();

    this.$el.html(template({
      i18n: this.i18n,
      isGridDisplay: this.isGridDisplay,
      isVideo: this.isVideo,
      isMultimediaAd: this.isMultimediaAd,
      isAdCreatorEnabled: this.isAdCreatorEnabled,
    }));

    const path = DependencyResolver.get('Constants').imagePath;
    const odata = DependencyResolver.get('component/http/channel/odata');
    const state = DependencyResolver.get('component/state/index');
    const config = DependencyResolver.get('component/config/index');
    const cid = config.get('CurrentAccount') ? config.get('CurrentAccount').AdvertiserCustomerID : config.get('advertiserCustomerID');
    const aid = this.isImproveMMA ? this.recommendationTargetAccountId : state.get('account_id');
    const uid = _.result(config.get('CurrentUser'), 'Id');
    const instrumentation = DependencyResolver.get('campaignui/feature/instrumentation/index');
    const preferencesService = DependencyResolver.get('component/preference/service/index');

    ReactDOM.render(
      <CrudAudienceAdReactWithContext
        ref={this.crudReactComponent}
        responsiveAdRepository={this.responsiveAdRepository}
        odata={odata}
        cid={`${cid}`}
        aid={`${aid}`}
        uid={`${uid}`}
        i18n={this.i18n}
        i18nErrors={this.i18nErrors}
        isMultimediaAdinMSANEnabled={this.isMultimediaAdinMSANEnabled}
        genericImage={this.isForVNext ? GenericImage : `${path}1.91to1.jpg`}
        viewContext={this.viewContext}
        pageContext={this.pageContext}
        isGridDisplay={this.isGridDisplay}
        onAdSaved={this.onAdSaved}
        scenario={this.scenario}
        ad={this.ad}
        biddingScheme={this.biddingScheme}
        perfMarker={this.perfMarker.createChild('ReactComp')}
        isForVNext={this.isForVNext}
        isAudienceCampaign={this.isAudienceCampaign}
        isAdRecommendationMSANEnabled={this.isAdRecommendationMSANEnabled}
        isMultimediaAd={this.isMultimediaAd}
        isPlaceholderMMA={this.isPlaceholderMMA}
        isStockPhotoEnabled
        oDataConfig={this.oDataConfig}
        isMobileUrlEnabled={this.isMobileUrlEnabled}
        isImpressionTrackingUrlsEnabled={this.isImpressionTrackingUrlsEnabled}
        activityLogger={this.activityLogger}
        permissions={this.permissions}
        isVideo={this.isVideo}
        isAdCreatorEnabled={this.isAdCreatorEnabled}
        isFinalUrlImageRetrievalEnabled={this.isFinalUrlImageRetrievalEnabled}
        instrumentation={instrumentation}
        enableOfflinePollingDelay={this.enableOfflinePollingDelay}
        preferencesService={preferencesService}
        hasWebsiteImageOptedInObservable={this.hasWebsiteImageOptedInObservable}
        newI18n={this.newI18n}
        parsedTextMaxLengthExtension={this.parsedTextMaxLengthExtension}
        isRecommendation={this.isRecommendation}
        edm={this.edm}
        jsdata={this.jsdata}
        isImproveMMA={this.isImproveMMA}
        isMMAV2Enabled={this.isMMAV2Enabled}
        serverContext={this.serverContext}
      />
      ,
      this.$('.react-image-experience')[0]
    );

    this.$('.error-no-created-ad').append(this.noCreatedAdErr.render().el);
    this.onRenderedInDOM();
    if (this.options && this.options.perfMarker && this.renderMarker) {
      this.renderMarker.done();
    }
    return this;
  }

  /**
   * Get the actions of the step section.
   * This is automatically called from the wizard.
   *
   * @readonly
   * @returns {Object} returns array that has information about the actions.
   */
  get actions() {
    const actions = [];

    if (this.isSwapStepsEnabled) {
      actions.push({
        name: 'done',
        htmlAttributes: {
          class: 'btn-primary',
        },
        enabled: true,
        method: 'saveAndExit',
        displayText: this.i18n.getString('Audience_Campaign_Save_Label'),
      });

      actions.push({
        name: 'back',
        displayText: this.i18n.getString('Audience_Campaign_Crud_Campaign_Back_Label'),
      });
    } else {
      actions.push({
        name: 'back',
        displayText: this.i18n.getString('Audience_Campaign_Crud_Campaign_Back_Label'),
      });

      actions.push({
        name: 'done',
        htmlAttributes: {
          class: 'btn-primary',
        },
        enabled: true,
        method: 'saveAndExit',
        displayText: this.i18n.getString('Audience_Campaign_Crud_Campaign_Save_Exit'),
      });

      if (this.scenario === 'adgroup-creation') {
      // use the actions as is
      } else if (this.scenario === 'campaign-creation') {
      // need the next button at the top.
        actions.unshift({
          name: 'next',
          displayText: this.i18n.getString('Audience_Campaign_Crud_Campaign_Save_Next'),
        });
      }
    }

    return actions;
  }

  saveAdAsync() {
    if (this.crudReactComponent.current) {
      return this.crudReactComponent.current.saveAdAsync()
        .finally(withUiBlock(this.wizardElement, this.i18n.getString('Loading')));
    }

    return Promise.resolve(true);
  }

  saveToClient() {
    if (this.crudReactComponent.current) {
      return this.crudReactComponent.current.saveToClient()
        .finally(withUiBlock(this.wizardElement, this.i18n.getString('Loading')));
    }

    return Promise.resolve(true);
  }

  /**
   * Checks if at least one ad was created.
   * @private
   *
   * @returns {boolean} true if there is at least one ad created, false if no ad was created
   */
  hasCreatedAd() {
    return this.crudReactComponent.current.hasCreatedAd();
  }

  /**
   * Checks if wizard can go to the next step
   *
   * @returns {Promise} Promise.resolve(true) if at leas one ad was created, Promise.reject()
   * if noad was created
   */
  wizardShouldGoNext() {
    return this.saveAdAsync().then((isSaved) => {
      if (!isSaved) {
        this.activityLogger.signal('clicked next: false', 'responsive ad editor');
        return Promise.reject();
      }

      if (!this.hasCreatedAd()) {
        this.noCreatedAdErr.setErrorMessage(this.i18n.getString('Audience_Campaign_Crud_NoCreatedAd'));
        this.activityLogger.signal('clicked next: false', 'responsive ad editor');
        return Promise.reject();
      }

      this.activityLogger.signal('clicked next: true', 'responsive ad editor');
      return true;
    });
  }

  /**
   * Always go back. But add a log.
   * @private
   *
   * @returns {Promise} always allow go back.
   */
  wizardShouldGoBack() {
    this.activityLogger.signal('clicked back: true', 'responsive ad editor');
    return new Promise(resolve => resolve(true));
  }

  /**
   * Triggers event 'saveAndExit' if at least one ad was created, otherwise it displays an error.
   * @private
   *
   * @returns {undefined}
   */
  saveAndExit() {
    return this.saveAdAsync().then((isSaved) => {
      if (!isSaved) {
        this.activityLogger.signal('save and exit: false', 'responsive ad editor');
        return Promise.reject();
      }

      if (!this.hasCreatedAd()) {
        this.activityLogger.signal('save and exit: false', 'responsive ad editor');
        this.noCreatedAdErr.setErrorMessage(this.i18n.getString('Audience_Campaign_Crud_NoCreatedAd'));
        return Promise.reject();
      }

      this.trigger('saveAndExit');
      if (this.isOpenCreatePanel && this.isLastStep) {
        const mes = { CampaignId: this.viewContext.currentCampaign.Id, DeliveryStatusType: 'NoEligibleAdGroups' };

        this.applyActivity.trace(JSON.stringify(mes));
      }
      this.activityLogger.signal('save and exit: true', 'responsive ad editor');
      return true;
    });
  }

  /**
   * Event handler for when ad is saved
   * @param {Object} savedAd saved ad
   * @param {string} imageUrl image url
   * @returns {undefined}
   */
  onAdSaved(savedAd, imageUrl) {
    this.noCreatedAdErr.removeErrors();

    this.trigger('onSave', { savedAd, imageUrl });
  }

  /**
   * Event handler on create another requested
   *
   * @returns {undefined}
   */
  onCreateAnother() {
    this.crudReactComponent.current.onCreateAd();
  }
}
